/* eslint-disable no-unused-expressions */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faImage } from '@fortawesome/free-solid-svg-icons';
import { client } from '../sanity/client.ts';

const Projects = () => {
    const [projects, setProjects] = useState([]);
    const [count, setCount] = useState(0);
    const targetCount = 20;
    const [expandedProject, setExpandedProject] = useState(null);

    useEffect(() => {
        // Fetch projects from Sanity, supporting both Sanity and Cloudinary images
        client.fetch(`
            *[_type == "project"]{
                _id,
                name,
                description,
                "coverImage": 
                  select(
                    mainImage.image.assetType == "sanity" => mainImage.image.sanityImage.asset->url,
                    mainImage.image.assetType == "cloudinary" => mainImage.image.cloudinaryImage.secure_url,
                    null
                  ),
                "coverAlt": mainImage.alt,
                additionalImages[]{
                  "url":
                    select(
                      image.assetType == "sanity" => image.sanityImage.asset->url,
                      image.assetType == "cloudinary" => image.cloudinaryImage.secure_url,
                      null
                    ),
                  alt,
                  caption
                }
            }
        `).then(data => setProjects(data));
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setCount(prev => {
                if (prev >= targetCount) {
                    clearInterval(timer);
                    return targetCount;
                }
                return prev + 1;
            });
        }, 100);
        return () => clearInterval(timer);
    }, []);

    // Toggle project expansion
    const toggleProject = (project) => {
        setExpandedProject(expandedProject?._id === project._id ? null : project);
    };

    // Handle image load error
    const handleImageError = (e) => {
        e.target.onerror = null;
        e.target.src = "https://placehold.co/800x600/ff0000/white?text=Image+Not+Found";
    };

    return (
        <div className="container mx-auto px-4 py-16 mt-8">
            <h2 className="text-4xl font-bold text-left mb-12 mt-12">Here are some of our key projects</h2>
            <div className="text-left mb-16">
                <div className="text-8xl font-bold text-primary">{count}+</div>
                <p className="text-xl mt-2">Projects Delivered</p>
            </div>
            <div className="space-y-6">
                {projects.map((project) => (
                    <div key={project._id} className="bg-white shadow-md overflow-hidden">
                        {/* Project Header - Always visible */}
                        <div
                            className={`flex items-center p-5 cursor-pointer gap-6 ${
                                expandedProject?._id === project._id ? 'bg-gray-100' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => toggleProject(project)}
                        >
                            {/* Project thumbnail */}
                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded overflow-hidden flex-shrink-0 mr-5 bg-gray-200 flex items-center justify-center">
                                {project.coverImage ? (
                                    <img
                                        src={project.coverImage}
                                        alt={project.coverAlt || project.name}
                                        className="w-full h-full object-cover"
                                        onError={handleImageError}
                                    />
                                ) : (
                                    <FontAwesomeIcon icon={faImage} className="text-gray-400 text-3xl" />
                                )}
                            </div>

                            {/* Project details container - with increased right padding */}
                            <div className="flex-grow pr-10">
                                <h3 className="text-lg sm:text-xl font-bold">{project.name}</h3>
                                <p className="text-sm text-gray-600">{project.description}</p>
                            </div>

                            {/* Arrow icon - with left padding for additional spacing */}
                            <div className="flex-shrink-0 ml-auto pl-6">
                                <FontAwesomeIcon
                                    icon={expandedProject?._id === project._id ? faArrowUp : faArrowDown}
                                    className="text-gray-600 text-lg"
                                />
                            </div>
                        </div>
                        
                        {/* Expandable Gallery Section */}
                        <AnimatePresence>
                            {expandedProject?._id === project._id && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="bg-gray-900 border-t border-gray-200"
                                >
                                    <div className="p-6">
                                        {/* Responsive grid for project images */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                            {(project.additionalImages || []).map((img, idx) => (
                                                <div
                                                    key={idx}
                                                    className="bg-gray-800 rounded flex items-center justify-center p-2"
                                                    style={{
                                                        aspectRatio: '1 / 1',
                                                        width: '100%',
                                                        minHeight: '200px',
                                                        maxHeight: '300px'
                                                    }}
                                                >
                                                    <img
                                                        src={img.url}
                                                        alt={img.alt || `${project.name} ${idx + 1}`}
                                                        className="w-full h-full object-cover rounded"
                                                        style={{
                                                            aspectRatio: '1 / 1',
                                                            width: '100%',
                                                            height: '100%'
                                                        }}
                                                        onError={handleImageError}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Projects;